import { StorageItem } from "@/core/entities/storage/StorageItem";
import { ISerializationPort, IStoragePort } from "@/core/ports";
import { StorageError } from "@/infrastructure/errors/StorageError";
import { SessionStorageManager } from "@/infrastructure/services/sessionStorage";

export class SessionStorageAdapter implements IStoragePort {
    private readonly storageManager: SessionStorageManager;

    constructor(
        private readonly serializer: ISerializationPort,
        prefix: string = "irApp"
    ) {
        this.storageManager = new SessionStorageManager(prefix);
    }

    async retrieve<T>(key: string, mustCheckExpiration?: boolean): Promise<StorageItem<T> | null> {
        try {
            const data = this.storageManager.get(key);
            if (!data) return null;
            const localItem = this.serializer.deserialize<StorageItem<T>>(data);
            const dataExpirationTime = localItem?.expirationTimeStamp;
            if ((dataExpirationTime && dataExpirationTime < Date.now()) || (mustCheckExpiration && !dataExpirationTime)) {
                this.storageManager.delete(key);
                return null;
            }
            return localItem;
        } catch (retrieveError: unknown) {
            if (retrieveError instanceof Error) {
                throw new StorageError(`Failed to retrieve item: ${retrieveError.message}`, key);
            }
            throw new StorageError("Failed to retrieve item", key);
        }
    }

    async store<T>(input: Pick<StorageItem<T>, "key" | "value">, expirationDuration?: number): Promise<void> {
        try {
            const item: StorageItem<T> = {
                ...input,
                timestamp: Date.now(),
            };
            if (expirationDuration) {
                const expirationDurationMilliseconds = expirationDuration * 1000;
                item.expirationTimeStamp = item.timestamp + expirationDurationMilliseconds;
            }
            const serialized = this.serializer.serialize(item);
            this.storageManager.set(item.key, serialized);
        } catch (storeError) {
            if (storeError instanceof Error) {
                throw new StorageError(`Failed to store item: ${storeError.message}`, input.key);
            } else {
                throw new StorageError("Failed to store item", input.key);
            }
        }
    }

    async delete(key: string): Promise<void> {
        try {
            this.storageManager.delete(key);
        } catch (error: unknown) {
            if (error instanceof Error) {
                throw new StorageError(`Failed to delete data from sessionStorage: ${error.message}`);
            }
            throw new StorageError('Failed to delete data from sessionStorage: Unknown error');
        }
    }

    async clear(): Promise<void> {
        try {
            this.storageManager.clear();
        } catch (error: unknown) {
            if (error instanceof Error) {
                throw new StorageError(`Failed to clear sessionStorage: ${error.message}`);
            }
            throw new StorageError('Failed to clear sessionStorage: Unknown error');
        }
    }
}

